// Color Conversions
export const convertColor = (input) => {
    let hex = "", rgb = "", hsl = "", r = 0, g = 0, b = 0;
    const str = input.trim().toLowerCase();
    
    try {
        if (str.startsWith('#')) {
            hex = str;
            if (hex.length === 4) { r = parseInt(hex[1]+hex[1],16); g = parseInt(hex[2]+hex[2],16); b = parseInt(hex[3]+hex[3],16); }
            else if (hex.length === 7) { r = parseInt(hex.substring(1,3),16); g = parseInt(hex.substring(3,5),16); b = parseInt(hex.substring(5,7),16); }
            rgb = `rgb(${r}, ${g}, ${b})`;
        } else if (str.startsWith('rgb')) {
            rgb = str;
            const parts = str.match(/\d+/g).map(Number);
            r = parts[0]; g = parts[1]; b = parts[2];
            hex = "#" + (1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1);
        } else {
            return "Invalid color format. Use #hex or rgb(r, g, b)";
        }
        
        let rNorm = r/255, gNorm = g/255, bNorm = b/255;
        let cmin = Math.min(rNorm, gNorm, bNorm), cmax = Math.max(rNorm, gNorm, bNorm), delta = cmax - cmin, h = 0, s = 0, l = 0;
        if (delta === 0) h = 0;
        else if (cmax === rNorm) h = ((gNorm - bNorm) / delta) % 6;
        else if (cmax === gNorm) h = (bNorm - rNorm) / delta + 2;
        else h = (rNorm - gNorm) / delta + 4;
        h = Math.round(h * 60);
        if (h < 0) h += 360;
        l = (cmax + cmin) / 2;
        s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
        s = +(s * 100).toFixed(1);
        l = +(l * 100).toFixed(1);
        hsl = `hsl(${h}, ${s}%, ${l}%)`;

        return `HEX: ${hex}\nRGB: ${rgb}\nHSL: ${hsl}`;
    } catch(e) {
        return "Invalid color format.";
    }
};

// Case Conversions
export const toCamelCase = (str) => str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase()).replace(/\s+/g, '');
export const toSnakeCase = (str) => str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(x => x.toLowerCase()).join('_') || str;
export const toPascalCase = (str) => str.match(/[a-z]+/gi)?.map(word => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()).join('') || str;
export const toKebabCase = (str) => str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(x => x.toLowerCase()).join('-') || str;
export const toConstantCase = (str) => str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(x => x.toUpperCase()).join('_') || str;

// Crypto Hash (Async)
export async function generateHashes(text) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    
    const sha1Buffer = await crypto.subtle.digest('SHA-1', data);
    const sha256Buffer = await crypto.subtle.digest('SHA-256', data);
    
    const sha1Hex = Array.from(new Uint8Array(sha1Buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    const sha256Hex = Array.from(new Uint8Array(sha256Buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    
    return { sha1: sha1Hex, sha256: sha256Hex };
}

// Mock Data
const firstNames = ['John', 'Jane', 'Alex', 'Emily', 'Chris', 'Katie', 'Mike', 'Sarah'];
const lastNames = ['Smith', 'Doe', 'Johnson', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore'];
const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'example.com', 'test.org'];
const departments = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Legal', 'Product'];
const roles = ['Manager', 'Developer', 'Designer', 'Analyst', 'Director', 'Coordinator'];
const streets = ['Main St', 'Maple Ave', 'Oak Ln', 'Cedar Rd', 'Elm St', 'Pine Dr'];
const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'London', 'Berlin'];

export const generateMockUsers = (count, fields = ['id', 'name', 'email', 'createdAt']) => {
    let users = [];
    for(let i=0; i<count; i++) {
        const first = firstNames[Math.floor(Math.random() * firstNames.length)];
        const last = lastNames[Math.floor(Math.random() * lastNames.length)];
        const domain = domains[Math.floor(Math.random() * domains.length)];
        
        let user = {};
        if (fields.includes('id')) user.id = crypto.randomUUID();
        if (fields.includes('name')) user.name = `${first} ${last}`;
        if (fields.includes('email')) user.email = `${first.toLowerCase()}.${last.toLowerCase()}@${domain}`;
        if (fields.includes('department')) user.department = departments[Math.floor(Math.random() * departments.length)];
        if (fields.includes('role')) user.role = roles[Math.floor(Math.random() * roles.length)];
        if (fields.includes('address')) user.address = `${Math.floor(Math.random() * 9999) + 1} ${streets[Math.floor(Math.random() * streets.length)]}, ${cities[Math.floor(Math.random() * cities.length)]}`;
        if (fields.includes('phone')) user.phone = `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`;
        if (fields.includes('createdAt')) user.createdAt = new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString();
        
        users.push(user);
    }
    return users;
};

export const formatJSON = (data, style) => {
    return style === 'Compressed' ? JSON.stringify(data) : JSON.stringify(data, null, 2);
};

export const formatCSV = (data, delimiter = '\\n') => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj => Object.values(obj).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const delim = delimiter === '\\r\\n' ? '\r\n' : '\n';
    return [headers, ...rows].join(delim);
};

export const formatSQL = (data, dialect = 'Postgres', tableName = 'mock_data') => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const columns = headers.join(', ');
    
    const rows = data.map(obj => {
        return '(' + Object.values(obj).map(v => {
            if (typeof v === 'string') return `'${v.replace(/'/g, "''")}'`;
            if (v === null || v === undefined) return 'NULL';
            return v;
        }).join(', ') + ')';
    });

    return `INSERT INTO ${tableName} (${columns}) VALUES\n${rows.join(',\n')};`;
};

export const formatPlainText = (data) => {
    return data.map((obj, i) => {
        return `Item ${i+1}:\n` + Object.entries(obj).map(([k, v]) => `  ${k}: ${v}`).join('\n');
    }).join('\n\n');
};

export const generateLoremIpsum = (paragraphs) => {
    const text = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.";
    return Array(paragraphs).fill(text).join('\n\n');
};
